import type {
	PullBlockReason,
	PullPhase,
} from '@Guidelines/Shell/PullToRefresh/Hooks/usePullToRefreshGesture';
import { PULL_REVEAL_PX } from '@Guidelines/Shell/PullToRefresh/resolvePullDistance';
import resolveRevealProgress from '@Guidelines/Shell/PullToRefresh/resolveRevealProgress';
import { theme } from '@Guidelines/Theme/theme';
import { css, keyframes } from '@emotion/react';
import { ArrowDown, Ban, RefreshCw, WifiOff } from 'lucide-react';
import type * as React from 'react';

type Props = {
	phase: PullPhase;
	distance: number;
	blockReason: PullBlockReason | null;
};

const spinKeyframes = keyframes`
	to {
		transform: rotate(360deg);
	}
`;

const PHASE_LABELS: Readonly<Record<'pulling' | 'armed' | 'refreshing', string>> = Object.freeze({
	pulling: 'Tirer pour actualiser',
	armed: 'Relâcher pour actualiser',
	refreshing: 'Actualisation…',
});

const BLOCK_LABELS: Readonly<Record<PullBlockReason, string>> = Object.freeze({
	capture: 'Actualisation impossible pendant un enregistrement',
	offline: 'Hors ligne : actualisation impossible',
});

const MIN_SCALE = 0.85;

const pillStyle = css`
	position: fixed;
	top: env(safe-area-inset-top, 0px);
	left: 50%;
	z-index: 1500;
	display: flex;
	align-items: center;
	gap: 0.5rem;
	max-width: calc(100vw - 2rem);
	padding: 0.5rem 1rem;
	border: 1.5px solid ${theme.colors.borderStrong};
	border-radius: ${theme.radii.pill};
	background: ${theme.colors.surface};
	color: ${theme.colors.ink};
	font-family: ${theme.fonts.body};
	font-size: 0.875rem;
	pointer-events: none;
`;

const settlingStyle = css`
	transition: transform 200ms ease-out;

	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}
`;

const blockedStyle = css`
	background: ${theme.states.danger.surface};
	color: ${theme.states.danger.ink};
`;

const arrowStyle = css`
	display: inline-flex;
	transition: transform 180ms ease-out;

	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}
`;

const refreshingIconStyle = css`
	display: inline-flex;
	animation: ${spinKeyframes} 1s linear infinite;

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`;

const PullToRefreshIndicator: React.FC<Props> = ({ phase, distance, blockReason }) => {
	if (phase === 'idle') {
		return null;
	}

	const isRefreshing = phase === 'refreshing';
	const progress = isRefreshing ? 1 : resolveRevealProgress(distance);
	const offset = Math.max(distance - PULL_REVEAL_PX, 0);
	const scale = isRefreshing ? 1 : MIN_SCALE + (1 - MIN_SCALE) * progress;
	const label = phase === 'blocked' ? BLOCK_LABELS[blockReason ?? 'capture'] : PHASE_LABELS[phase];

	let icon: React.ReactNode;

	if (phase === 'blocked') {
		icon =
			blockReason === 'offline' ? (
				<WifiOff size={18} aria-hidden="true" />
			) : (
				<Ban size={18} aria-hidden="true" />
			);
	} else if (isRefreshing) {
		icon = (
			<span css={refreshingIconStyle}>
				<RefreshCw size={18} aria-hidden="true" />
			</span>
		);
	} else {
		icon = (
			<span css={arrowStyle} style={{ transform: `rotate(${phase === 'armed' ? 180 : 0}deg)` }}>
				<ArrowDown size={18} aria-hidden="true" />
			</span>
		);
	}

	return (
		<div
			role="status"
			aria-live="polite"
			css={[
				pillStyle,
				isRefreshing ? settlingStyle : undefined,
				phase === 'blocked' ? blockedStyle : undefined,
			]}
			style={{
				transform: `translate(-50%, ${offset}px) scale(${scale})`,
				opacity: progress,
			}}
		>
			{icon}
			<span>{label}</span>
		</div>
	);
};

export default PullToRefreshIndicator;
